import format from 'date-fns/format'

import type { TBookingContext, TCartGroup, TCartGroupOffer, TCartGroupPetAddon } from '@providers/booking/types'
import { formatNumberAsPrice, genId } from 'components-ui/src/shared/common'
import type { TWaitListOffer, TWaitListOfferAddOn } from './types'

const getPetNames = (group: TCartGroup) => group.pets.map((pet) => pet.name).join(', ')

const getServiceTypeNameById = (ctx: TBookingContext, serviceTypeId?: string) => {
  return ctx.data.serviceTypes?.find((serviceType) => serviceType.id === (serviceTypeId || ''))?.serviceType?.name
}

const getPriceValue = (item: TCartGroupOffer) => item.availability.availabilities?.[0]?.price?.value ?? 0

const getBoardingPrice = (group: TCartGroup) => {
  return formatNumberAsPrice(group.offerSelected?.availability.availabilities?.[0]?.price?.value ?? 0)
}

const getRoomAddOns = (group: TCartGroup): TWaitListOfferAddOn[] => {
  return (group.roomsAddOn ?? []).map((addOn) => ({
    id: genId(),
    offerType: addOn.offer.type || 'SERVICE',
    offerName: addOn.offer.displayName || '',
    petName: getPetNames(group),
    price: formatNumberAsPrice(getPriceValue(addOn)),
  }))
}

const getRequiredAddOns = (group: TCartGroup): TWaitListOfferAddOn[] => {
  return group.requiredAddOns.reduce<TWaitListOfferAddOn[]>((addOns, addOn, petIdx) => {
    const pet = group.pets[petIdx]
    if (!addOn || !pet) return addOns

    addOns.push({
      id: genId(),
      offerType: 'SERVICE-REQUIRED',
      offerName: addOn.offer.displayName || '',
      petName: pet.name,
      price: formatNumberAsPrice(getPriceValue(addOn)),
    })
    return addOns
  }, [])
}

const getPetAddOns = (group: TCartGroup): TWaitListOfferAddOn[] => {
  return (group.petsAddOn ?? []).map((addOn) => {
    const details = addOn.pets.reduce(
      (result, pet, petIdx) => {
        const groupPet = group.pets.find(({ id }) => id === pet.id)
        if (!groupPet || pet.qty === 0) return result

        const qty = pet.qty > 1 ? ` x${pet.qty}` : ''
        result.names.push(`${groupPet.name}${qty}`)
        result.price += pet.qty * (addOn.availability.availabilities?.[petIdx]?.price?.value ?? 0)
        return result
      },
      { names: [] as string[], price: 0 }
    )

    return {
      id: genId(),
      offerType: addOn.offer.type || 'PET',
      offerName: addOn.offer.displayName || '',
      petName: details.names.join(', '),
      price: formatNumberAsPrice(details.price),
    }
  })
}

const getDaycarePetAddOns = (group: TCartGroup): TWaitListOfferAddOn[] => {
  return (group.petsAddOnDaycare ?? []).map((addOn) => {
    const details = addOn.pets.reduce(
      (result, pet, petIdx) => {
        const groupPet = group.pets.find(({ id }) => id === pet.id)
        if (!groupPet) return result

        const quantity = pet.dates.reduce((total, date) => total + date.qty, 0)
        if (quantity === 0) return result

        result.names.push(`${groupPet.name}${quantity > 1 ? ` x${quantity}` : ''}`)
        result.price += pet.dates.reduce(
          (total, date) => total + date.qty * (date.availability.availabilities?.[petIdx]?.price?.value ?? 0),
          0
        )
        return result
      },
      { names: [] as string[], price: 0 }
    )

    return {
      id: genId(),
      offerType: addOn.offer.type || 'PET',
      offerName: addOn.offer.displayName || '',
      petName: details.names.join(', '),
      price: formatNumberAsPrice(details.price),
    }
  })
}

const buildSelectedOffer = (ctx: TBookingContext, group: TCartGroup, serviceType: string): TWaitListOffer => {
  const selected = group.offerSelected!.offer
  let period = ''
  let price = ''

  switch (ctx.serviceTypeName) {
    case 'boarding':
      period = `${format(ctx.startDate!, 'MMM d')} - ${format(ctx.endDate!, 'MMM d')}`.toUpperCase()
      price = getBoardingPrice(group)
      break
    case 'daycare':
      period = format(group.daycareSelected![0].date, 'MMM d').toUpperCase()
      price = formatNumberAsPrice(group.daycareSelected![0].availability.availabilities?.[0]?.price?.value ?? 0)
      break
  }

  return {
    id: genId(),
    serviceType,
    offerName: selected.displayName || '',
    period,
    petName: getPetNames(group),
    price,
    addOns: [
      ...getRequiredAddOns(group),
      ...getRoomAddOns(group),
      ...getPetAddOns(group),
      ...getDaycarePetAddOns(group),
    ],
  }
}

const getGroomAddOns = (group: TCartGroup, petIdx: number, petName: string): TWaitListOfferAddOn[] => {
  return (group.groomAddOn?.[petIdx] ?? []).map((addOn: TCartGroupPetAddon) => ({
    id: genId(),
    offerType: 'grooming',
    offerName: addOn.offer.displayName || '',
    petName,
    price: formatNumberAsPrice(addOn.availability.availabilities?.[0]?.price?.rate ?? 0),
  }))
}

const getGrooms = (group: TCartGroup): TWaitListOffer[] => {
  return group.grooms.flatMap((groom, petIdx) => {
    const petName = group.pets[petIdx]?.name || ''
    const lastOfferIdx = groom.offers.length - 1

    return groom.offers.map(({ offer, availabilityGroups }, offerIdx) => ({
      id: genId(),
      serviceType: 'grooming',
      offerName: offer.displayName || '',
      period: '',
      petName,
      price: formatNumberAsPrice(availabilityGroups.availabilities?.[0]?.price?.rate ?? 0),
      addOns: offerIdx === lastOfferIdx ? getGroomAddOns(group, petIdx, petName) : [],
    }))
  })
}

export const buildWaitListOffers = (ctx: TBookingContext): TWaitListOffer[] => {
  return ctx.cart.groups.reduce<TWaitListOffer[]>((offers, group) => {
    if (group.offerSelected) {
      const serviceType = getServiceTypeNameById(ctx, group.offerSelected.offer.locationServiceTypeId)
      if (serviceType) offers.push(buildSelectedOffer(ctx, group, serviceType))
    }

    offers.push(...getGrooms(group))
    return offers
  }, [])
}
